import type {
  ComposerActivity,
  ComposerOptions,
  ComposerVoice,
} from "../../components/composer/Composer";
import type {
  DisplayMessage,
  DisplayConversation,
  GenerationStatusDetails,
  StreamEvent,
} from "./ChatPage";
import { api } from "../../api";
import { applyQueueSnapshot } from "./conversationQueueState";
import {
  supportsActivity,
  type MessageActivityEvent,
  type Model,
  type ModelPreferences,
  type SubagentRun,
  type TodoListSnapshot,
  type ToolEvent,
} from "@lumen/shared";

const runtimeEnv =
  (
    import.meta as ImportMeta & {
      env?: Record<string, string | boolean | undefined>;
    }
  ).env ?? {};
const API_BASE = (runtimeEnv.VITE_API_BASE || "/api").replace(/\/$/, "");

export { supportsActivity } from "@lumen/shared";

export function normalizeProjectComposerDefaults(
  defaults: Partial<ComposerOptions> | undefined,
): Partial<ComposerOptions> {
  const workspaceIds = Array.isArray(defaults?.agentWorkspaceIds)
    ? defaults.agentWorkspaceIds
    : typeof defaults?.agentWorkspaceId === "string" &&
        defaults.agentWorkspaceId
      ? [defaults.agentWorkspaceId]
      : [];
  const workspaceId =
    defaults?.agentWorkspaceId !== undefined
      ? defaults.agentWorkspaceId
      : workspaceIds.length === 1
        ? workspaceIds[0]
        : null;
  const repositoryIds =
    defaults?.repositoryIds ??
    (workspaceId && defaults?.repositoryId
      ? { [workspaceId]: [defaults.repositoryId] }
      : {});

  return {
    ...(defaults ?? {}),
    toolIds: defaults?.toolIds ?? [],
    skillIds: defaults?.skillIds ?? [],
    mcpServerIds: defaults?.mcpServerIds ?? [],
    mcpToolSelections: defaults?.mcpToolSelections ?? {},
    generationParameters: defaults?.generationParameters ?? {},
    agentWorkspaceIds: workspaceIds,
    agentWorkspaceId: workspaceId,
    repositoryIds,
    repositoryId: defaults?.repositoryId ?? null,
  };
}

export function projectComposerDefaults(
  draftProjectId: string | null,
  conversation: DisplayConversation | null,
  hasLatestUserMessage: boolean,
  draftDefaults: Partial<ComposerOptions> | undefined,
): Partial<ComposerOptions> | undefined {
  const defaults = draftProjectId
    ? draftDefaults
    : conversation?.surface === "project" && !hasLatestUserMessage
      ? (conversation.composerDefaults as Partial<ComposerOptions> | undefined)
      : undefined;
  return draftProjectId ||
    (conversation?.surface === "project" && !hasLatestUserMessage)
    ? normalizeProjectComposerDefaults(defaults)
    : defaults;
}

export class StreamDisconnectedError extends Error {
  constructor(message = "The live stream disconnected") {
    super(message);
    this.name = "StreamDisconnectedError";
  }
}

export function modelVoices(model: Model): ComposerVoice[] {
  const metadata =
    model.remoteMetadata && typeof model.remoteMetadata === "object"
      ? (model.remoteMetadata as Record<string, unknown>)
      : {};
  const values = metadata.supported_voices ?? metadata.supportedVoices;
  if (!Array.isArray(values)) return [];
  return values.flatMap((value) => {
    if (typeof value === "string") return [{ id: value, label: value }];
    if (!value || typeof value !== "object") return [];
    const item = value as Record<string, unknown>;
    const id = [item.id, item.voice].find(
      (candidate): candidate is string => typeof candidate === "string",
    );
    if (!id) return [];
    const label = typeof item.name === "string" ? item.name : id;
    return [{ id, label }];
  });
}

/**
 * Resolves the model that should be used for a mode: the last model used for
 * that mode, then its per-mode default, then the global/admin defaults, and
 * finally the first compatible model. This keeps every mode's model separate
 * and ensures a mode with a default never falls back to the first list entry.
 */
export function preferredActivityModelId(
  models: Model[],
  activity: ComposerActivity,
  remembered: Partial<Record<ComposerActivity, string>>,
  preferences: ModelPreferences | undefined,
): string | null {
  const eligible = models.filter((model) => supportsActivity(model, activity));
  if (!eligible.length) return null;
  const candidates = [
    remembered[activity],
    preferences?.activityDefaultModelIds?.[activity],
    ...(activity === "ask" ? [preferences?.defaultModelId] : []),
    preferences?.adminDefaultModelId,
  ];
  for (const candidate of candidates) {
    if (candidate && eligible.some((model) => model.id === candidate))
      return candidate;
  }
  return eligible[0].id;
}

/**
 * Per-mode models to attach to a message so generation (and regeneration and
 * editing) keep using each mode's own model instead of the conversation's
 * Ask model.
 */
export function delegatedActivityModelIdsFrom(
  map: Partial<Record<ComposerActivity, string>>,
): NonNullable<
  NonNullable<ComposerOptions["generationParameters"]>["activityModelIds"]
> {
  return Object.fromEntries(
    Object.entries(map).filter(
      ([candidate, value]) => candidate !== "ask" && Boolean(value),
    ),
  ) as NonNullable<
    NonNullable<ComposerOptions["generationParameters"]>["activityModelIds"]
  >;
}

export function upsertStreamMessage(
  messages: DisplayMessage[],
  incoming: DisplayMessage,
) {
  const existing = messages.find((message) => message.id === incoming.id);
  const message = existing
    ? mergeLiveAssistantMessage(existing, incoming)
    : incoming;
  // SSE events are emitted by several server paths (initial response, queue
  // drain, and reconnect). Their arrival order is not a conversation order.
  // Always place a message next to its parent so a late user event cannot
  // render below the assistant response it belongs to.
  const next = messages.filter((candidate) => candidate.id !== message.id);
  const parentIndex = message.parentId
    ? next.findIndex((candidate) => candidate.id === message.parentId)
    : -1;
  const childIndex = next.findIndex(
    (candidate) => candidate.parentId === message.id,
  );
  const createdAt = new Date(message.createdAt).getTime();
  const chronologicalIndex = Number.isFinite(createdAt)
    ? next.findIndex(
        (candidate) => new Date(candidate.createdAt).getTime() > createdAt,
      )
    : -1;
  const insertAt =
    parentIndex >= 0
      ? parentIndex + 1
      : childIndex >= 0
        ? childIndex
        : chronologicalIndex >= 0
          ? chronologicalIndex
          : next.length;
  next.splice(insertAt, 0, message);
  return next;
}

export function appendMessageActivity(
  message: DisplayMessage,
  type: "text" | "reasoning",
  content: string,
) {
  if (!content) return message;
  const activityEvents = [...(message.activityEvents ?? [])];
  const previous = activityEvents.at(-1);
  if (previous?.type === type)
    activityEvents[activityEvents.length - 1] = {
      ...previous,
      content: previous.content + content,
    };
  else activityEvents.push({ id: crypto.randomUUID(), type, content });
  return { ...message, activityEvents };
}

/**
 * Applies a streamed text/reasoning checkpoint once. A status frame can carry
 * the full durable message and be dispatched before a buffered delta frame;
 * in that case appending the delta again would duplicate the visible answer.
 */
export function appendMessageDelta(
  message: DisplayMessage,
  type: "text" | "reasoning",
  content: string,
  start?: number,
) {
  if (!content) return message;
  const current = type === "text" ? message.content : (message.reasoning ?? "");
  // Absolute stream offsets distinguish a replayed checkpoint from legitimate
  // repeated text, including when a checkpoint already contains part of a delta.
  if (start !== undefined) {
    const overlap = Math.max(0, current.length - start);
    if (overlap >= content.length) return message;
    return appendMessageActivity(
      {
        ...message,
        ...(type === "text"
          ? { content: current + content.slice(overlap) }
          : { reasoning: current + content.slice(overlap) }),
      },
      type,
      content.slice(overlap),
    );
  }
  const checkpointedActivityText = (message.activityEvents ?? [])
    .filter(
      (
        event,
      ): event is Extract<
        MessageActivityEvent,
        { type: "text" | "reasoning" }
      > => event.type === type,
    )
    .map((event) => event.content)
    .join("");
  const hasCheckpointedActivity = checkpointedActivityText === current;
  if (
    hasCheckpointedActivity &&
    current.endsWith(content) &&
    current.length >= content.length
  )
    return message;
  return appendMessageActivity(
    {
      ...message,
      ...(type === "text"
        ? { content: current + content }
        : { reasoning: current + content }),
    },
    type,
    content,
  );
}

export function todoListSignature(snapshot: TodoListSnapshot) {
  return JSON.stringify([
    snapshot.completed,
    snapshot.total,
    snapshot.explanation ?? "",
    snapshot.todos.map((todo) => `${todo.status}:${todo.content}`),
  ]);
}

export function updateMessageToolActivity(
  message: DisplayMessage,
  tool: ToolEvent,
) {
  const activityEvents = [...(message.activityEvents ?? [])];
  const index = activityEvents.findIndex(
    (event) => event.type === "tool" && event.tool.id === tool.id,
  );
  if (index >= 0)
    activityEvents[index] = {
      ...activityEvents[index],
      tool,
    } as MessageActivityEvent;
  else activityEvents.push({ id: `tool-${tool.id}`, type: "tool", tool });
  return { ...message, activityEvents };
}

export function updateSubagentRun(
  conversation: DisplayConversation,
  run: SubagentRun,
) {
  const messages = [...(conversation.messages ?? [])];
  const index = messages.findIndex(
    (message) => message.id === run.assistantMessageId,
  );
  if (index < 0) return conversation;
  const runs = [...(messages[index].subagentRuns ?? [])];
  const runIndex = runs.findIndex((item) => item.id === run.id);
  if (runIndex >= 0) runs[runIndex] = run;
  else runs.push(run);
  messages[index] = { ...messages[index], subagentRuns: runs };
  return { ...conversation, messages };
}

export function mergeLiveAssistantMessage(
  previous: DisplayMessage,
  next: DisplayMessage,
) {
  if (next.activityDeferred)
    return {
      ...next,
      toolEvents: next.toolEvents ?? [],
      activityEvents: next.activityEvents ?? [],
    };
  const contentRegressed =
    previous.content.length > next.content.length &&
    previous.content.startsWith(next.content);
  const previousReasoning = previous.reasoning ?? "";
  const nextReasoning = next.reasoning ?? "";
  const reasoningRegressed =
    previousReasoning.length > nextReasoning.length &&
    previousReasoning.startsWith(nextReasoning);
  const statusRank = (status: DisplayMessage["generationStatus"]) =>
    status === "completed" || status === "failed" || status === "cancelled"
      ? 3
      : status === "awaiting-approval" || status === "awaiting-question"
        ? 2
        : status === "running"
          ? 1
          : 0;
  const mergedToolEvents = [...(next.toolEvents ?? [])];
  const toolIndexes = new Map(
    mergedToolEvents.map((event, index) => [event.id, index]),
  );
  for (const event of previous.toolEvents ?? []) {
    const index = toolIndexes.get(event.id);
    if (index === undefined) {
      toolIndexes.set(event.id, mergedToolEvents.length);
      mergedToolEvents.push(event);
      continue;
    }
    const current = mergedToolEvents[index];
    const currentRank =
      current.status === "completed" || current.status === "failed"
        ? 3
        : current.status === "awaiting-approval" ||
            current.status === "awaiting-question"
          ? 2
          : 1;
    const previousRank =
      event.status === "completed" || event.status === "failed"
        ? 3
        : event.status === "awaiting-approval" ||
            event.status === "awaiting-question"
          ? 2
          : 1;
    if (previousRank > currentRank) mergedToolEvents[index] = event;
  }
  const activityEvents =
    contentRegressed || reasoningRegressed
      ? previous.activityEvents
      : next.activityEvents?.length
        ? next.activityEvents
        : previous.activityEvents;
  const mergedActivityEvents = [...(activityEvents ?? [])].map((event) =>
    event.type === "tool"
      ? {
          ...event,
          tool:
            mergedToolEvents.find((tool) => tool.id === event.tool.id) ??
            event.tool,
        }
      : event,
  );
  // Keep the server/live timeline order. Canonical toolEvents can be only a
  // tail of the history; appending those calls would move them after all text.
  // MessageActivity recovers pending interactions separately when necessary.
  return {
    ...previous,
    ...next,
    activityDeferred: next.activityDeferred ?? false,
    content: contentRegressed ? previous.content : next.content,
    reasoning: reasoningRegressed ? previous.reasoning : next.reasoning,
    toolEvents: mergedToolEvents,
    activityEvents: mergedActivityEvents,
    generationStatus:
      statusRank(previous.generationStatus) > statusRank(next.generationStatus)
        ? previous.generationStatus
        : next.generationStatus,
    subagentRuns: next.subagentRuns?.length
      ? next.subagentRuns
      : previous.subagentRuns,
  };
}

export function mergeStreamSnapshot(
  conversation: DisplayConversation,
  message: DisplayMessage,
) {
  const messages = [...(conversation.messages ?? [])];
  const index = messages.findIndex((item) => item.id === message.id);
  if (index >= 0)
    messages[index] = mergeLiveAssistantMessage(messages[index], message);
  else {
    const parentIndex = message.parentId
      ? messages.findIndex((item) => item.id === message.parentId)
      : -1;
    messages.splice(
      parentIndex >= 0 ? parentIndex + 1 : messages.length,
      0,
      message,
    );
  }
  return { ...conversation, messages };
}

type StreamConversationUpdater = (
  update: (current: DisplayConversation) => DisplayConversation,
) => void;
type StreamConversationSetter = (
  update: (current: DisplayConversation | null) => DisplayConversation | null,
) => void;

export function createConversationStreamEventHandler(options: {
  conversationId: string;
  setConversation: StreamConversationSetter;
  assistantMessageId: { current: string | null };
  applyConversationSnapshot: (
    current: DisplayConversation | null,
    incoming: DisplayConversation,
  ) => DisplayConversation;
  setUsageRevision: (update: (value: number) => number) => void;
  setError: (message: string) => void;
  mergeLatestMessages?: (
    current: DisplayConversation,
    latest: DisplayConversation,
  ) => DisplayConversation;
}) {
  return (event: StreamEvent) => {
    applyConversationStreamEvent(event, {
      updateConversation: (update) =>
        options.setConversation((current) =>
          current?.id === options.conversationId ? update(current) : current,
        ),
      setConversation: options.setConversation,
      assistantMessageId: options.assistantMessageId,
      applyConversationSnapshot: options.applyConversationSnapshot,
      mergeLatestMessages: options.mergeLatestMessages,
      conversationId: options.conversationId,
    });
    if (event.type === "done" || event.type === "cancelled")
      options.setUsageRevision((revision) => revision + 1);
    if (event.type === "error") options.setError(event.error);
  };
}

/**
 * Applies every conversation event emitted by a generation stream. The
 * request lifecycle is intentionally kept outside this function; send,
 * continue, approval, edit, regenerate, subagent, compaction, and reconnect
 * only provide their operation-specific assistant merge when needed.
 */
export function applyConversationStreamEvent(
  event: StreamEvent,
  options: {
    updateConversation: StreamConversationUpdater;
    setConversation: StreamConversationSetter;
    assistantMessageId: { current: string | null };
    applyConversationSnapshot: (
      current: DisplayConversation | null,
      incoming: DisplayConversation,
    ) => DisplayConversation;
    mergeLatestMessages?: (
      current: DisplayConversation,
      latest: DisplayConversation,
    ) => DisplayConversation;
    conversationId?: string;
    optimisticMessageId?: string;
    mergeAssistantMessage?: (
      messages: DisplayMessage[],
      incoming: DisplayMessage,
    ) => DisplayMessage[];
  },
) {
  const isVisible = (conversation: DisplayConversation) =>
    !options.conversationId || conversation.id === options.conversationId;
  const updateVisible = (
    update: (current: DisplayConversation) => DisplayConversation,
  ) =>
    options.updateConversation((current) =>
      isVisible(current) ? update(current) : current,
    );
  const setSnapshot = (incoming: DisplayConversation) =>
    options.setConversation((current) => {
      if (
        current &&
        options.conversationId &&
        current.id !== options.conversationId
      )
        return current;
      if (!current) return options.applyConversationSnapshot(null, incoming);
      return options.mergeLatestMessages
        ? options.mergeLatestMessages(current, incoming)
        : options.applyConversationSnapshot(current, incoming);
    });
  const assistantIndex = (messages: DisplayMessage[]) => {
    const targetId = options.assistantMessageId.current;
    const targetedIndex = targetId
      ? messages.findIndex((message) => message.id === targetId)
      : -1;
    if (targetedIndex >= 0 && messages[targetedIndex].role === "assistant")
      return targetedIndex;
    for (let index = messages.length - 1; index >= 0; index -= 1)
      if (messages[index].role === "assistant") return index;
    return -1;
  };

  if (event.type === "sync") {
    if (event.message.role === "assistant")
      options.assistantMessageId.current = event.message.id;
    updateVisible((current) => mergeStreamSnapshot(current, event.message));
    return;
  }
  if (event.type === "queue") {
    updateVisible((current) =>
      applyQueueSnapshot(current, current.id, event.queue, event.revision),
    );
    return;
  }
  if (event.type === "user") {
    updateVisible((current) => ({
      ...current,
      messages: upsertStreamMessage(
        (current.messages ?? []).filter(
          (message) => message.id !== options.optimisticMessageId,
        ),
        event.message,
      ),
    }));
    return;
  }
  if (event.type === "assistant") {
    options.assistantMessageId.current = event.message.id;
    updateVisible((current) => ({
      ...current,
      messages: options.mergeAssistantMessage
        ? options.mergeAssistantMessage(current.messages ?? [], event.message)
        : upsertStreamMessage(current.messages ?? [], event.message),
    }));
    return;
  }
  if (event.type === "rollover") {
    // A message split advances the generation to a fresh assistant fragment.
    // The follow stream emits the fragment as an assistant event first, so it
    // is already rendered; re-point the delta/tool target at the fragment that
    // is now streaming.
    options.assistantMessageId.current = event.assistantMessageId;
    return;
  }
  if (event.type === "edited") {
    options.setConversation((current) =>
      options.applyConversationSnapshot(current, event.conversation),
    );
    return;
  }
  if (event.type === "delta" || event.type === "reasoning") {
    updateVisible((current) => {
      const messages = [...(current.messages ?? [])];
      const index = assistantIndex(messages);
      if (index >= 0)
        messages[index] = appendMessageDelta(
          messages[index],
          event.type === "delta" ? "text" : "reasoning",
          event.content,
          event.start,
        );
      return { ...current, messages };
    });
    return;
  }
  if (event.type === "tool") {
    updateVisible((current) => {
      const messages = [...(current.messages ?? [])];
      const index = assistantIndex(messages);
      if (index >= 0) {
        const toolEvents = [...(messages[index].toolEvents ?? [])];
        const eventIndex = toolEvents.findIndex(
          (item) => item.id === event.event.id,
        );
        if (eventIndex >= 0) toolEvents[eventIndex] = event.event;
        else toolEvents.push(event.event);
        messages[index] = updateMessageToolActivity(
          { ...messages[index], toolEvents },
          event.event,
        );
      }
      return { ...current, messages };
    });
    return;
  }
  if (event.type === "subagent") {
    updateVisible((current) => updateSubagentRun(current, event.run));
    return;
  }
  if (event.type === "question-waiting") {
    if (event.message) {
      if (event.message.role === "assistant")
        options.assistantMessageId.current = event.message.id;
      updateVisible((current) => mergeStreamSnapshot(current, event.message!));
    }
    return;
  }
  if (event.type === "done" || event.type === "cancelled") {
    if (event.conversation) setSnapshot(event.conversation);
    return;
  }
  if (event.type === "error") {
    if (event.conversation) setSnapshot(event.conversation);
  }
}

type StreamRequestOptions = {
  /** Stable key for replaying the initial POST after an ambiguous response. */
  idempotencyKey?: string;
  reconnect?: (assistantId?: string) => Promise<string | null>;
  /** Maximum time without even an SSE heartbeat before reconnecting. */
  idleTimeoutMs?: number;
  /** Set to a finite value only for callers that should eventually give up. */
  maxReconnectAttempts?: number;
  isStale?: () => boolean;
  onAttention?: (kind: "completed" | "approval") => void;
  notifyOnDone?: boolean;
  /** The approval-decision stream ends when another approval remains pending. */
  stopOnApprovalWaiting?: boolean;
  onDebug?: (details: StreamDebugDetails) => void;
  onDegraded?: (reason: string) => void;
  onRecovered?: () => void;
  degradedDelayMs?: number;
  debugLabel?: string;
};

type StreamDebugDetails = {
  phase: "retry" | "failed";
  label?: string;
  path: string;
  attempt: number;
  lastEventId: number;
  reason: string;
  status?: number;
};

function statusDetailsFromEvent(
  event: Extract<StreamEvent, { type: "status" }>,
): GenerationStatusDetails {
  return {
    phase: event.phase ?? (event.degraded ? "degraded" : "running"),
    jobStatus: event.jobStatus,
    correlationId: event.correlationId,
    attempt: event.attempt,
    retryAttempt: event.retryAttempt,
    capacityRetryCount: event.capacityRetryCount,
    providerRetryCount: event.providerRetryCount,
    retryCategory: event.retryCategory,
    queuePosition: event.queuePosition,
    queueReason: event.queueReason,
    nextRetryAt: event.nextRetryAt,
    workerState: event.workerState,
    recoveryState: event.recoveryState,
    checkpointed: event.checkpointed,
    degraded: event.degraded,
    reason: event.reason,
    failureCategory: event.failureCategory,
    failureReason: event.failureReason,
  };
}

export function waitForStreamRetry(delay: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("The stream was aborted", "AbortError"));
      return;
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, delay);
    const onAbort = () => {
      window.clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      reject(new DOMException("The stream was aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export async function reconnectConversationStream(
  conversationId: string,
  assistantId?: string,
) {
  let messageId = assistantId;
  if (!messageId) {
    const conversation = await api<DisplayConversation>(
      `/conversations/${conversationId}?limit=1`,
    );
    messageId =
      conversation.generationStatus === "running"
        ? (conversation.activeLeafMessageId ?? undefined)
        : [...(conversation.messages ?? [])]
            .reverse()
            .find(
              (message) =>
                message.role === "assistant" &&
                message.generationStatus === "running",
            )?.id;
  }
  return messageId
    ? `/conversations/${conversationId}/messages/stream?messageId=${encodeURIComponent(messageId)}`
    : null;
}

export async function streamRequest(
  path: string,
  body: unknown | undefined,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal,
  options: StreamRequestOptions = {},
) {
  let currentPath = path;
  let currentBody = body;
  let assistantId: string | undefined;
  let reconnectAttempts = 0;
  let reconnecting = false;
  let degraded = false;
  let degradedTimer: number | undefined;
  let lastEventId = 0;
  // A generation stream is long-lived by design, but a missing/deleted
  // generation must not leave a browser retrying forever. Callers with a
  // different lifecycle can still opt into an explicit finite budget.
  const maxReconnectAttempts = options.maxReconnectAttempts ?? 5;
  const idleTimeoutMs = options.idleTimeoutMs ?? 45_000;
  const retryDelays = [500, 1_000, 2_000, 4_000, 8_000, 15_000, 30_000];
  const reportDebug = (details: StreamDebugDetails) => {
    options.onDebug?.(details);
    const log = details.phase === "failed" ? console.error : console.warn;
    log("[Lumen stream]", details);
  };
  const clearDegradedTimer = () => {
    if (degradedTimer === undefined) return;
    window.clearTimeout(degradedTimer);
    degradedTimer = undefined;
  };
  const notifyDegraded = (reason: string) => {
    clearDegradedTimer();
    if (degraded) return;
    degraded = true;
    options.onDegraded?.(reason);
  };
  const scheduleDegraded = (reason: string) => {
    if (degraded || degradedTimer !== undefined) return;
    degradedTimer = window.setTimeout(
      () => {
        degradedTimer = undefined;
        notifyDegraded(reason);
      },
      Math.max(0, options.degradedDelayMs ?? 3_000),
    );
  };

  while (true) {
    let responseReceived = false;
    let readerStarted = false;
    let terminalEventReceived = false;
    let eventDispatchFailed = false;
    let responseStatus: number | undefined;
    let idleTimedOut = false;
    let idleTimer: number | undefined;
    let attemptController: AbortController | undefined;
    let removeOuterAbortListener = () => {};
    const clearIdleTimer = () => {
      if (idleTimer === undefined) return;
      window.clearTimeout(idleTimer);
      idleTimer = undefined;
    };
    const armIdleTimer = () => {
      clearIdleTimer();
      idleTimer = window.setTimeout(
        () => {
          idleTimedOut = true;
          attemptController?.abort();
        },
        Math.max(1, idleTimeoutMs),
      );
    };
    try {
      attemptController = new AbortController();
      if (signal?.aborted) attemptController.abort();
      else if (signal) {
        const abortAttempt = () => attemptController?.abort();
        signal.addEventListener("abort", abortAttempt, { once: true });
        removeOuterAbortListener = () =>
          signal.removeEventListener("abort", abortAttempt);
      }
      armIdleTimer();
      const response = await fetch(`${API_BASE}${currentPath}`, {
        method: currentBody === undefined ? "GET" : "POST",
        credentials: "include",
        headers: {
          accept: "text/event-stream",
          ...(lastEventId > 0 ? { "last-event-id": String(lastEventId) } : {}),
          ...(currentBody === undefined
            ? {}
            : {
                "content-type": "application/json",
                ...(options.idempotencyKey
                  ? { "idempotency-key": options.idempotencyKey }
                  : {}),
              }),
        },
        ...(currentBody === undefined
          ? {}
          : { body: JSON.stringify(currentBody) }),
        signal: attemptController.signal,
      });
      responseReceived = true;
      responseStatus = response.status;
      if (!response.ok) {
        const value = await response.json().catch(() => ({}));
        const error =
          typeof value.error === "string" && currentBody !== undefined
            ? value.error
            : `Request failed (${response.status})`;
        throw new Error(error);
      }
      if (reconnecting) {
        reconnecting = false;
        clearDegradedTimer();
        if (degraded) {
          degraded = false;
          options.onRecovered?.();
        }
      }
      if (!response.body)
        throw new Error("Streaming is not supported by this browser");
      const reader = response.body.getReader();
      readerStarted = true;
      const decoder = new TextDecoder();
      let buffer = "";
      const pendingText: Extract<
        StreamEvent,
        { type: "delta" | "reasoning" }
      >[] = [];
      const pendingSubagents = new Map<
        string,
        Extract<StreamEvent, { type: "subagent" }>
      >();
      let frame = 0;
      const dispatch = (event: StreamEvent) => {
        if (signal?.aborted || options.isStale?.()) return;
        if (
          (event.type === "status" || event.type === "error") &&
          event.degraded
        ) {
          notifyDegraded(event.reason ?? "Generation status became degraded");
        } else if (
          degraded &&
          (event.type === "status" ||
            event.type === "done" ||
            event.type === "cancelled" ||
            event.type === "approval-waiting" ||
            event.type === "question-waiting")
        ) {
          if (degraded) {
            degraded = false;
            options.onRecovered?.();
          }
        }
        if (event.type === "deleted" || event.type === "superseded")
          notifyDegraded(event.reason);
        // Status events carry the authoritative durable message projection.
        // Feed it through the normal merge path so callers that only handle
        // deltas/tools still render queue, waiting, and checkpoint updates.
        if (event.type === "status" && event.message)
          onEvent({
            type: "sync",
            message: {
              ...event.message,
              generationStatusDetails: statusDetailsFromEvent(event),
            },
          });
        if (
          (event.type === "assistant" || event.type === "sync") &&
          event.message.role === "assistant"
        )
          assistantId = event.message.id;
        // A message split (rollover) advances the generation to a fresh
        // assistant fragment. Keep the reconnect target pointed at the newest
        // fragment so a disconnect after a rollover re-attaches to the row
        // that is actually streaming.
        if (event.type === "rollover") assistantId = event.assistantMessageId;
        if (
          event.type === "done" ||
          event.type === "cancelled" ||
          event.type === "error" ||
          event.type === "deleted" ||
          event.type === "superseded" ||
          event.type === "approval-waiting" ||
          event.type === "question-waiting"
        )
          terminalEventReceived = true;
        if (
          event.type === "approval-waiting" ||
          event.type === "question-waiting" ||
          (event.type === "tool" &&
            event.event.status === "awaiting-approval") ||
          (event.type === "subagent" &&
            (event.run.status === "awaiting-approval" ||
              event.run.status === "awaiting-question"))
        )
          options.onAttention?.("approval");
        if (event.type === "done" && options.notifyOnDone)
          options.onAttention?.("completed");
        try {
          onEvent(event);
        } catch (error) {
          eventDispatchFailed = true;
          throw error;
        }
      };
      const flushText = () => {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        for (const event of pendingText.splice(0)) dispatch(event);
        for (const event of pendingSubagents.values()) dispatch(event);
        pendingSubagents.clear();
      };
      const queueText = (
        event: Extract<StreamEvent, { type: "delta" | "reasoning" }>,
      ) => {
        const previous = pendingText.at(-1);
        if (
          previous?.type === event.type &&
          ((previous.start === undefined && event.start === undefined) ||
            (previous.start !== undefined &&
              event.start === previous.start + previous.content.length))
        )
          previous.content += event.content;
        else pendingText.push({ ...event });
        if (!frame) frame = requestAnimationFrame(flushText);
      };
      const consume = (block: string) => {
        const eventIdText = block.match(/^id:\s*(\d+)$/m)?.[1];
        if (eventIdText) {
          const eventId = Number(eventIdText);
          if (!Number.isFinite(eventId) || eventId <= lastEventId) return;
          lastEventId = eventId;
        }
        const data = block
          .split("\n")
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trimStart())
          .join("\n");
        if (!data || data === "[DONE]") return;
        const event = JSON.parse(data) as StreamEvent;
        if (event.type === "delta" || event.type === "reasoning")
          queueText(event);
        else if (event.type === "subagent") {
          pendingSubagents.set(event.run.id, event);
          if (!frame) frame = requestAnimationFrame(flushText);
        } else {
          flushText();
          dispatch(event);
        }
      };
      while (true) {
        const { done, value } = await reader.read();
        armIdleTimer();
        buffer += decoder
          .decode(value, { stream: !done })
          .replace(/\r\n/g, "\n");
        let boundary = buffer.indexOf("\n\n");
        while (boundary >= 0) {
          consume(buffer.slice(0, boundary));
          buffer = buffer.slice(boundary + 2);
          boundary = buffer.indexOf("\n\n");
        }
        if (done) break;
      }
      if (buffer.trim()) consume(buffer);
      flushText();
      if (!terminalEventReceived && !signal?.aborted)
        throw new StreamDisconnectedError();
      clearIdleTimer();
      removeOuterAbortListener();
      return;
    } catch (reason) {
      clearIdleTimer();
      removeOuterAbortListener();
      const retryable =
        !terminalEventReceived &&
        !eventDispatchFailed &&
        ((idleTimedOut && !signal?.aborted) ||
          (!isAbortError(reason) && (!responseReceived || readerStarted)));
      if (
        !retryable ||
        !options.reconnect ||
        reconnectAttempts >= maxReconnectAttempts
      ) {
        if (retryable) {
          const message = "The live generation stream disconnected";
          notifyDegraded(message);
          reportDebug({
            phase: "failed",
            label: options.debugLabel,
            path: currentPath,
            attempt: reconnectAttempts,
            lastEventId,
            reason: message,
            status: responseStatus,
          });
          throw new StreamDisconnectedError(
            `Live updates stopped after ${reconnectAttempts} reconnect attempts ` +
              `(last event ${lastEventId}). ` +
              "The generation may still be running; reload the conversation to reconnect.",
          );
        }
        throw reason;
      }

      const attempt = reconnectAttempts + 1;
      scheduleDegraded("The live generation stream disconnected");
      reconnecting = true;
      reportDebug({
        phase: "retry",
        label: options.debugLabel,
        path: currentPath,
        attempt,
        lastEventId,
        reason: "The live generation stream disconnected",
        status: responseStatus,
      });
      let nextPath: string | null = null;
      while (!nextPath) {
        try {
          nextPath = await options.reconnect(assistantId);
          if (!nextPath) break;
        } catch (reconnectReason) {
          reportDebug({
            phase: "retry",
            label: options.debugLabel,
            path: currentPath,
            attempt,
            lastEventId,
            reason: "Reconnect lookup failed",
          });
          await waitForStreamRetry(
            retryDelays[Math.min(reconnectAttempts, retryDelays.length - 1)],
            signal,
          );
          reconnectAttempts = attempt;
          if (reconnectAttempts >= maxReconnectAttempts)
            throw new StreamDisconnectedError(
              `Live updates stopped after ${reconnectAttempts} reconnect attempts ` +
                `(last event ${lastEventId}). ` +
                "The generation may still be running; reload the conversation to reconnect.",
            );
        }
      }
      // The initial send is the only stream that has a replay-safe POST. If
      // the browser loses that request before the server has made the
      // assistant discoverable, the lookup quite correctly returns no stream
      // to follow. Replaying the POST with its idempotency key lets the server
      // either create the turn or attach to the already-created one. Without
      // this branch the optimistic user message remained on screen while the
      // request was dropped and a reload could not recover it.
      const replayInitialRequest =
        !nextPath &&
        currentBody !== undefined &&
        Boolean(options.idempotencyKey);
      if (!nextPath && !replayInitialRequest) throw reason;
      await waitForStreamRetry(
        retryDelays[Math.min(reconnectAttempts, retryDelays.length - 1)],
        signal,
      );
      reconnectAttempts = attempt;
      if (nextPath) {
        currentPath = nextPath;
        currentBody = undefined;
      }
    }
  }
}

export function isAbortError(reason: unknown) {
  return reason instanceof Error && reason.name === "AbortError";
}

export function isTextEntryTarget(target: EventTarget | null) {
  const element = target instanceof HTMLElement ? target : null;
  return Boolean(
    element?.isContentEditable ||
    element?.tagName === "INPUT" ||
    element?.tagName === "TEXTAREA" ||
    element?.tagName === "SELECT",
  );
}

export const composerActivities: ComposerActivity[] = [
  "ask",
  "image",
  "audio",
  "video",
  "speech",
  "transcription",
  "model3d",
  "sprite",
  "tileset",
];
